// CanvasStage — レイアウト非依存の永続「キャンバス舞台」
// (docs/_wip/modern-layout.md §9.7 engine 永続化)。
//
// ## なぜ存在するか
// レイアウト切替 (Classic ⇄ Modern) で `App.tsx RoomLayout` がレイアウトごと
// remount すると、`DrawingCanvas` が新しい canvas 要素 → 新しい `CanvasDrawingEngine`
// を生成し、内容がクリア + 切替の窓で他者 stroke を取りこぼして desync する。
// §9.7 採用方針 A は engine/canvas を **切替の上位に hoist して永続化** することで
// この問題を根絶する。CanvasStage はその「永続される canvas 領域」= ②に当たる:
//   ① engine 1 個 (registerEngine は 1 回) … chrome の再 render では再登録しない
//   ② CanvasStage = scroll コンテナ + zoom ラッパ + DrawingCanvas + overlay 群
//   ③ 描画状態 store … RoomLayout が useRoomChrome として所有 (props で渡す)
//
// ## DOM 配置 = 計測フローティング・スロット方式 (§9.7)
// CanvasStage は RoomLayout 直下に `position: absolute` で置かれ、アクティブな
// chrome の「空スロット div」の矩形 (= props.rect、useSlotRect が計測) に重なる。
// chrome の見た目を一切変えずに canvas を chrome の上に正確に乗せる。canvas バッファ
// は固定 px なので、囲うコンテナサイズが変わっても描画ピクセルは不変。
//
// ## engine 1 回登録の担保
// `onEngineReady` は RoomLayout が所有する `chrome.onEngineReady` (= SessionProvider の
// registerEngine を呼ぶ)。RoomLayout 直下に CanvasStage を置けば、chrome (ModernLayout 等)
// が再 render しても stage は remount せず、engine 登録は DrawingCanvas のマウント時 1 回
// だけになる (= engineRef が切替で null にならず、取りこぼし窓が構造的に消える)。

import type { CustomCursor, StrokePen } from '@oekaki/protocol';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { Transport } from '../../application/ports/Transport.js';
import { reportTouchGesture } from '../../application/touchGestureAnalytics.js';
import type { RoomState } from '../../domain/RoomState.js';
// 2026-09-29 viewport transform 移行 (決定5、modern-phone-shell-2026-07-02.md §6):
// pan/pinch/resize-correction/初期表示の純粋ロジック。
import {
  clampViewportForTransform,
  measureSlot,
  viewportAfterResizeRotated,
} from '../../infrastructure/drawing/viewport.js';
import {
  createRotationTracker,
  ROTATION_DRAG_THRESHOLD_DEG,
  ROTATION_SNAP_DEG,
  type RotationTracker,
  vectorAngleDeg,
} from '../../infrastructure/drawing/viewRotation.js';
// M1-a 入室時フィット (決定5 §6 で CanvasStage へ移設): スマホ (viewport 幅 < 640
// ‖ coarse pointer × 短辺 < 640) のときだけ初期 zoom を fit-to-viewport にする判定
// (layoutResolution の「スマホ = Modern 固定」と同じ境界)。
import { useConnectionLocked } from '../providers/ConnectionLockContext.js';
// engine ブリッジ / canvas オーバーレイ (= paint UI でなく engine 接続部、§9.6 共有層)。
import { ARROW_CURSOR_CSS_VALUE } from './classic/arrowCursorGlyph.js';
import { BrushCursorOverlay } from './classic/BrushCursorOverlay.js';
import { DraftBubbleOverlay } from './classic/DraftBubbleOverlay.js';
import { DrawingCanvas } from './classic/DrawingCanvas.js';
import type { LastKnownPosition } from './classic/draftBubbleOverlay.js';
import { ImagepasteOverlay } from './classic/ImagepasteOverlay.js';
import { type ImagepasteToast, ImagepasteToastOverlay } from './classic/ImagepasteToastOverlay.js';
import { createLeadLineSource } from './classic/leadLine.js';
import { NameLabelOverlay } from './classic/NameLabelOverlay.js';
import {
  PEN_ONLY_FINGER_NOTICE_DISPLAY_MS,
  PEN_ONLY_FINGER_NOTICE_TEXT,
} from './classic/penOnlyMode.js';
import type { SlotRect } from './floatingSlot.js';
import { LockedCursorTag } from './LockedCursorTag.js';
import { isPhoneViewport } from './layoutResolution.js';
import { ReconnectOverlay } from './ReconnectOverlay.js';
import { DimmedLayerBadge } from './shared/DimmedLayerBadge.js';
import { LayerSoloBadge } from './shared/LayerSoloBadge.js';
import type { RegionVariant } from './shared/RegionMoveControls.js';
import { RegionMoveOverlay } from './shared/RegionMoveOverlay.js';
import type { RoomChrome } from './shared/useRoomChrome.js';
import { readPhoneViewportInput } from './useLayoutPreference.js';
import { useShiftSpaceRotateHold } from './useShiftSpaceRotateHold.js';

export interface CanvasStageProps {
  readonly state: RoomState;
  readonly transport: Transport;
  /** 描画状態 store + engine 接続 (RoomLayout が所有する useRoomChrome の戻り値)。 */
  readonly chrome: RoomChrome;
  /**
   * スロット計測結果 (= useSlotRect)。null のうちは可視化しない (= まだ chrome の
   * 空スロットが計測できていない初期フレーム)。確定後は absolute でその矩形に重ねる。
   */
  readonly rect: SlotRect | null;
  /**
   * overflow scroll の anchor (= pan / プレビュー scroll 同期で chrome 側パネルが
   * 読む)。RoomLayout が所有し、stage の scroll コンテナと chrome のプレビューで共有する。
   */
  readonly scrollRef: React.RefObject<HTMLDivElement | null>;
  /**
   * custom-cursor-2026-09-19.md §2.5-4 (time-permitting bonus):「作成中バブル」用の
   * transient state (`SessionView.cursorDrafts` をそのまま渡せる形)。未指定/空なら
   * バブルは一切出ない (= 呼び出し側が配線しなくても壊れない、既存挙動を保つ)。
   */
  readonly cursorDrafts?: Readonly<Record<string, CustomCursor>>;
  /** 自分の線を描き終えた瞬間の通知 (困りごとのコツの本数カウント用、DrawingCanvas へそのまま渡す)。 */
  readonly onOwnStrokeEnd?: (info: { readonly pen: StrokePen; readonly width: number }) => void;
  /**
   * §9.7 Phase 2: 手のひら (pan) モードが有効か。CanvasStage は scroll コンテナを
   * 所有するので、Classic の Space hold / pan トグルによるドラッグスクロール pan は
   * ここで実装する (= 旧 ClassicLayout の onCanvasWrapperPointer* + cursor grab/grabbing
   * + pointerEvents:none を移設)。Classic は `spaceHeld || panToggleOn` を渡す。
   * 未指定 (= Modern) なら pan しない (= Modern の現挙動を維持、native scrollbar のみ)。
   */
  readonly panActive?: boolean;
  /**
   * §9.7 Phase 2: scroll コンテナ (`<main>`) の枠 className 一式。canvas 領域の枠
   * (border 種別 / 背景 / padding / 角丸) はレイアウトごとに見た目が違う
   * (Modern = rounded-lg border、Classic = outsetFrame bevel) ため、各レイアウトが
   * 自分の枠を渡して見た目を不変に保つ。border の **色** はマスク ON で上書きするため、
   * idle / maskActive の 2 状態を分けて渡す:
   *   - `frameBaseClassName`: border 種別・背景・padding・角丸など (色以外)。
   *   - `frameMaskBorderClassName`: マスク ON 時の border 色クラス。
   *   - `frameIdleBorderClassName`: マスク OFF 時の border 色クラス。
   * 未指定なら Modern の既定枠 (= Phase 1 の挙動) を使う。
   */
  readonly frameBaseClassName?: string;
  readonly frameMaskBorderClassName?: string;
  readonly frameIdleBorderClassName?: string;
  /** 範囲の移動・コピーのボタン群の見た目 (既定 Modern。Classic は 'classic')。 */
  readonly regionVariant?: RegionVariant;
}

/**
 * 永続キャンバス舞台。RoomLayout 直下に absolute 配置し、アクティブ chrome の
 * 空スロット矩形 (props.rect) に重なる。
 */
/** PC 回転ドラッグで、表示枠中央からこの距離 (px) 未満のポインタは角度が不安定なので無視する。 */
const MIN_ROTATE_DRAG_RADIUS_PX = 8;

export const CanvasStage = ({
  state,
  transport,
  chrome,
  rect,
  scrollRef,
  cursorDrafts,
  onOwnStrokeEnd,
  panActive = false,
  // 既定枠 = Modern の rounded-lg border (= Phase 1 の挙動)。Classic は outsetFrame を渡す。
  frameBaseClassName = 'rounded-lg border bg-[var(--color-bg-soft)]/40 p-3',
  frameMaskBorderClassName = 'border-[var(--color-accent)]',
  frameIdleBorderClassName = 'border-[var(--color-border)]',
  regionVariant = 'modern',
}: CanvasStageProps): React.ReactNode => {
  const maskActive = chrome.pen.mask.type !== 'none' && chrome.pen.mask.colors.length > 0;

  // custom-cursor-2026-09-19.md §2.5: `state.members[]` (RoomMember, protocol
  // shape) already carries each member's confirmed `cursor` — this stage is
  // the single mount point for every overlay that needs it (§2.5-1 self /
  // §2.5-2 peers / §2.5-4 draft bubble). `cursorDrafts` (transient, §2.3) is
  // read straight from the prop — only the draft bubble's ANCHOR position
  // needs stage-local memory (below).
  const members = state.members;
  const ownCursor = useMemo(
    () => members.find((m) => m.uid === state.uid)?.cursor ?? null,
    [members, state.uid],
  );
  const cursorForUid = useCallback(
    (uid: string) => members.find((m) => m.uid === uid)?.cursor ?? null,
    [members],
  );

  // §2.5-4 (time-permitting bonus)「作成中バブル」: 相手がエディタを開いている間は
  // 描画していないことが多いため、`chrome.nameLabels` の 1 秒 TTL より長生きする
  // 「最後に観測したストローク位置」を uid ごとに覚えておく必要がある。ref (state
  // ではない) — 更新のたびに再レンダリングさせる必要はなく、バブル自体は
  // `cursorDrafts` (props) の変化で再レンダリングされる。
  const lastKnownPositionsRef = useRef<Map<string, LastKnownPosition>>(new Map());
  useEffect(() => {
    for (const label of chrome.nameLabels) {
      lastKnownPositionsRef.current.set(label.uid, { x: label.x, y: label.y });
    }
  }, [chrome.nameLabels]);
  const lastKnownPositionForUid = useCallback(
    (uid: string) => lastKnownPositionsRef.current.get(uid) ?? null,
    [],
  );

  // §9.8.1 表示専用リード線: DrawingCanvas (publisher) ⇄ LeadLineOverlay
  // (subscriber) を繋ぐ ref 流儀の source を stage に 1 個持つ。engine と同じく
  // stage は切替で remount しないので source も永続 (= stroke を跨いで安定)。
  const leadLineSource = useMemo(() => createLeadLineSource(), []);
  // 手ぶれ補正 ON のときだけリードを出す。Classic は stabilizer を露出しない
  // (= 常に 'off') ので enabled=false ⇒ LeadLineOverlay が完全 no-op。
  const leadLineEnabled = chrome.stabilizerLevel !== 'off';

  // 2026-07-25 矢印カーソル方式 B′ (consult 確定): ストローク進行中かどうかを
  // ローカル state でも保持する。DrawingCanvas の `onStrokeActiveChange` は元々
  // chrome.setStrokeActive (プレビュー抑制用の ref) だけに配線されていたが、
  // 同じ発火源 (= pointer capture と一致する stroke lifecycle) を
  //   ① innerWrapper の `data-oekaki-stroking` 属性 (styles.css がストローク中
  //      だけ canvas を `cursor: none` にして矢印モードのネイティブカーソルを
  //      隠す、二重矢印防止)
  //   ② BrushCursorOverlay の `strokeActive` prop (矢印グリフの表示条件)
  // にも使う。stroke 開始/終了でしか変わらない低頻度値なので useState で問題ない
  // (= 同ファイル内の isPanDragging と同じ流儀)。
  const [isStroking, setIsStroking] = useState<boolean>(false);

  // 再接続中は描けない (docs/_wip/reconnect-draw-lock-2026-10-12.md)。判定は context の
  // 単一 boolean。ロック中のタップは DrawingCanvas の pointerdown 入口から通知され、
  // ReconnectOverlay が帯を即表示 + 点滅する (トーストは作らない)。
  const connectionLocked = useConnectionLocked();
  const [lockedTapKey, setLockedTapKey] = useState<number>(0);
  const handleLockedPointerDown = useCallback((): void => setLockedTapKey((k) => k + 1), []);
  // ペン専用モード中に指が無視されたときの短命通知 (feature/pen-only-finger-notice)。
  // DrawingCanvas の pointerup 時のみ呼ばれる (描画ホットパス外)。Classic/Modern/phone 共通。
  const [fingerToast, setFingerToast] = useState<ImagepasteToast | null>(null);
  const fingerToastIdRef = useRef(0);
  const handlePenOnlyFingerIgnored = useCallback((): void => {
    fingerToastIdRef.current += 1;
    setFingerToast({
      id: fingerToastIdRef.current,
      text: PEN_ONLY_FINGER_NOTICE_TEXT,
      countdown: false,
    });
  }, []);
  const dismissFingerToast = useCallback((): void => setFingerToast(null), []);
  const handleStrokeActiveChange = useCallback(
    (active: boolean): void => {
      chrome.setStrokeActive(active);
      setIsStroking(active);
    },
    [chrome.setStrokeActive],
  );

  // §9.7 Phase 2 (2026-09-29 viewport transform 移行、決定5、§6 で書き替え):
  // 手のひらモードのドラッグ pan (Classic 原典 onCanvasWrapperPointer* を移設)。
  // 旧方式は scroll コンテナを programmatic scrollLeft/scrollTop で動かしていたが、
  // `<main>` は `overflow: hidden` になった (= scroll 自体が無い) ため、
  // `chrome.setViewport` (= `{tx,ty}` の唯一の書き込み経路) を直接呼ぶ。inner
  // wrapper への pointerEvents:none 遮断 (= 既に start 済の stroke は engine 側で
  // 完結) は不変。
  const [isPanDragging, setIsPanDragging] = useState<boolean>(false);
  const panStartRef = useRef<{
    clientX: number;
    clientY: number;
    tx: number;
    ty: number;
    pointerId: number;
  } | null>(null);
  // canvas-rotation 段取り 2 (PC): 回転ドラッグ。`R` = 回転ツール (chrome.rotateToolActive、
  // スポイトと同じ持続ツール) / `Shift+Space` を押している間だけ一時切り替え。中心は表示枠中央、
  // ドラッグ位置の角度変化で回す (しきい値なし・吸着あり)。優先順位:
  //   Shift+Space (回転) > Space / 手のひら (pan) > 回転ツール (回転) > 通常 (描画)
  // = `Space` だけなら従来どおり手のひら、回転ツール中でも Space 押下中は手のひら。
  const shiftSpaceRotate = useShiftSpaceRotateHold();
  const rotateMode = shiftSpaceRotate || (!panActive && chrome.rotateToolActive);
  const panMode = panActive && !shiftSpaceRotate;
  const dragModeActive = panMode || rotateMode;
  const rotateStartRef = useRef<{
    readonly pointerId: number;
    /** 最初に角度が取れたフレームで生成する (中央に近すぎる開始は角度が不安定なため)。 */
    tracker: RotationTracker | null;
  } | null>(null);

  // pan / 回転モードが完全 OFF になった瞬間に、途中状態の panStartRef / rotateStartRef /
  // pointer capture / isPanDragging を確実に片付ける (Classic 原典の cleanup useEffect 踏襲)。
  useEffect(() => {
    if (dragModeActive) return;
    const wrapper = scrollRef.current;
    const start = panStartRef.current ?? rotateStartRef.current;
    if (wrapper !== null && start !== null && wrapper.hasPointerCapture(start.pointerId)) {
      wrapper.releasePointerCapture(start.pointerId);
    }
    panStartRef.current = null;
    rotateStartRef.current = null;
    setIsPanDragging(false);
  }, [dragModeActive, scrollRef]);

  /** 表示枠中央 (client 座標) から見たポインタの角度 (度)。中央に近すぎれば null (角度が不安定)。 */
  const pointerAngleFromSlotCenter = (clientX: number, clientY: number): number | null => {
    const wrapper = scrollRef.current;
    if (wrapper === null) return null;
    const slot = measureSlot(wrapper);
    const o = chrome.viewMapper.getSlotOrigin();
    const cx = o.x + slot.slotWidth / 2;
    const cy = o.y + slot.slotHeight / 2;
    if (Math.hypot(clientX - cx, clientY - cy) < MIN_ROTATE_DRAG_RADIUS_PX) return null;
    return vectorAngleDeg(cx, cy, clientX, clientY);
  };

  const newRotateTracker = (startAngle: number): RotationTracker =>
    createRotationTracker({
      startRotation: chrome.getRotation(),
      startAngle,
      thresholdDeg: ROTATION_DRAG_THRESHOLD_DEG,
      snapDeg: ROTATION_SNAP_DEG,
    });

  const onWrapperPointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (!dragModeActive) return;
    const wrapper = scrollRef.current;
    if (wrapper === null) return;
    if (panStartRef.current !== null || rotateStartRef.current !== null) return;
    e.preventDefault();
    e.stopPropagation();
    if (rotateMode) {
      rotateStartRef.current = { pointerId: e.pointerId, tracker: null };
      const angle = pointerAngleFromSlotCenter(e.clientX, e.clientY);
      if (angle !== null) rotateStartRef.current.tracker = newRotateTracker(angle);
    } else {
      const v = chrome.viewportRef.current;
      panStartRef.current = {
        clientX: e.clientX,
        clientY: e.clientY,
        tx: v.tx,
        ty: v.ty,
        pointerId: e.pointerId,
      };
    }
    wrapper.setPointerCapture(e.pointerId);
    setIsPanDragging(true);
  };
  const onWrapperPointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (!isPanDragging) return;
    const wrapper = scrollRef.current;
    if (wrapper === null) return;
    const rot = rotateStartRef.current;
    if (rot !== null) {
      if (e.pointerId !== rot.pointerId) return;
      const angle = pointerAngleFromSlotCenter(e.clientX, e.clientY);
      if (angle === null) return;
      if (rot.tracker === null) {
        rot.tracker = newRotateTracker(angle);
        return;
      }
      const next = rot.tracker.update(angle);
      if (next !== null) chrome.rotateToAtSlotCenter(next);
      return;
    }
    const start = panStartRef.current;
    if (start === null) return;
    const slot = measureSlot(wrapper);
    const t = chrome.viewMapper.getTransform();
    chrome.setViewport(
      clampViewportForTransform(
        {
          ...t,
          tx: start.tx + (e.clientX - start.clientX),
          ty: start.ty + (e.clientY - start.clientY),
        },
        slot.slotWidth,
        slot.slotHeight,
      ),
    );
  };
  const onWrapperPointerUp = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (!isPanDragging) return;
    const wrapper = scrollRef.current;
    if (wrapper?.hasPointerCapture(e.pointerId)) {
      wrapper.releasePointerCapture(e.pointerId);
    }
    panStartRef.current = null;
    rotateStartRef.current = null;
    setIsPanDragging(false);
  };

  const panCursorClass = dragModeActive ? (isPanDragging ? 'cursor-grabbing' : 'cursor-grab') : '';

  // 2026-06-12 zoom-to-cursor (素のホイール=ズーム, shortcut-settings.md §3 適用例):
  // canvas の表示枠 (= `<main>`、scrollRef) に **非 passive** な wheel listener を
  // 付け, `e.preventDefault()` でブラウザのページズーム/ページスクロールを止めてから
  // chrome.handleZoomAtPointer を呼ぶ。React の onWheel は passive 既定で preventDefault が
  // 効かない環境があるため明示的に addEventListener({ passive:false }) を使う。listener は
  // この `<main>` だけに付くので, チャット等パネル上のホイールには一切影響しない (= スコープ
  // = canvas エリア限定)。pan ドラッグ中はホイールズームを抑止 (= 操作の混線回避)。
  // deltaY<0 (上回し)=ズームイン, deltaY>0=ズームアウト, 1 ノッチ=1 段 (+/- キーと同モデル)。
  const handleZoomAtPointer = chrome.handleZoomAtPointer;
  useEffect(() => {
    const wrapper = scrollRef.current;
    if (wrapper === null) return;
    const onWheel = (e: WheelEvent): void => {
      // 修飾キー不要 (素のホイール)。ブラウザの Ctrl+ホイール page-zoom もここで吸収。
      e.preventDefault();
      if (isPanDragging) return;
      if (e.deltaY === 0) return;
      const rect = wrapper.getBoundingClientRect();
      const pointerX = e.clientX - rect.left;
      const pointerY = e.clientY - rect.top;
      const direction: 1 | -1 = e.deltaY < 0 ? 1 : -1;
      handleZoomAtPointer(direction, pointerX, pointerY);
    };
    wrapper.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      wrapper.removeEventListener('wheel', onWheel);
    };
  }, [scrollRef, handleZoomAtPointer, isPanDragging]);

  // M1-c タッチジェスチャ (2本指パン + ピンチ、modern-phone-shell §3.1、2026-09-29
  // viewport transform 移行で pan の適用先を書き替え):
  // 猶予窓 + 2指追跡は DrawingCanvas (pointer 経路の所有者) が行い、stage は
  // 「表示枠 + zoom/viewport API の持ち主」として適用面だけを提供する。
  //   - パン: 2指重心の移動量 (client px) をそのまま tx/ty に加算 (= 「指に内容が
  //     追従する」符号、旧 scrollLeft の逆符号関係と違い直感的に加算で良い)。
  //   - ピンチ: 重心の client 座標をコンテナ相対へ変換し、連続倍率の絶対値で
  //     `chrome.handleZoomToAtPointer` (M1-a API) を呼ぶ (= wheel ズームと同じ
  //     座標規約、clamp は API 側)。
  const onGesturePan = useCallback(
    (dx: number, dy: number): void => {
      const wrapper = scrollRef.current;
      if (wrapper === null) return;
      const slot = measureSlot(wrapper);
      const t = chrome.viewMapper.getTransform();
      chrome.setViewport(
        clampViewportForTransform(
          { ...t, tx: t.tx + dx, ty: t.ty + dy },
          slot.slotWidth,
          slot.slotHeight,
        ),
      );
    },
    [scrollRef, chrome],
  );
  // modern-phone-shell §7: タッチジェスチャ計測 (fire-and-forget、Classic/Modern 共通)。
  const onTouchGestureAnalytics = useCallback(
    (outcome: 'started' | 'late', delayMs?: number): void => {
      reportTouchGesture(transport, outcome, delayMs);
    },
    [transport],
  );
  const handleZoomToAtPointer = chrome.handleZoomToAtPointer;
  const onGestureZoomTo = useCallback(
    (nextZoom: number, clientX: number, clientY: number): void => {
      const wrapper = scrollRef.current;
      if (wrapper === null) return;
      const wrapperRect = wrapper.getBoundingClientRect();
      handleZoomToAtPointer(nextZoom, clientX - wrapperRect.left, clientY - wrapperRect.top);
    },
    [scrollRef, handleZoomToAtPointer],
  );
  // ひねり (canvas-rotation 段取り 2): 絶対回転角 + 回転軸 (2 本指重心の client 座標)。
  // 同じフレームで pan → zoom → rotate の順に呼ばれ、いずれも重心の真下の canvas 点を保つ。
  const rotateToAtClient = chrome.rotateToAtClient;
  const onGestureRotateTo = useCallback(
    (nextRotation: number, clientX: number, clientY: number): void => {
      rotateToAtClient(nextRotation, clientX, clientY);
    },
    [rotateToAtClient],
  );

  // 2026-09-29 viewport transform 移行 (決定5、§6): 表示枠 (`rect`) のサイズが
  // 初めて計測できたとき (= engine ready + 入室) と、その後サイズが変わったとき
  // (パネル開閉・回転・ウィンドウ resize・Classic⇄Modern 切替) の両方をここで扱う
  // — CanvasStage が `rect` (= `useSlotRect`、ResizeObserver 駆動で既に全リサイズ
  // 経路に反応する) を持つ唯一の場所なので、「計測を持つ場所が計測駆動の副作用を
  // 持つ」構造に揃える。
  //   - 初回 (= この engine でまだ適用していない): モバイルは `applyFitZoom`
  //     (zoom も変える)、デスクトップは `centerViewportAtCurrentZoom` (zoom は
  //     現状維持、canvas が枠より小さい軸だけ中央寄せ、決定5「入室時 (1x) は
  //     中央寄せ」)。
  //   - 2 回目以降 (= 既に初期化済でサイズが変わった): 旧 slot 中央にあった
  //     canvas 点を新 slot 中央にも保つ (`viewportAfterResize`、決定5「枠 resize
  //     時は枠中央の canvas 点を保つ」)。
  // engine が null に戻ってから新しい engine が来た場合も、オブジェクト identity
  // 比較で自然に「初回」扱いに戻る (= 旧 `initialFitEngineRef` と同じ latch 方式)。
  //
  // useLayoutEffect (fable-advisor 指摘): `rect` が null→非null になった同じ
  // コミットで `visibility: hidden → visible` も切り替わる (JSX 側)。ここが
  // useEffect (= paint 後に非同期実行) だと、visibility が先に visible になった
  // 状態 (= transform 未適用、tx=ty=0 の素の flex 位置) のままブラウザが 1 frame
  // 描画してから transform が当たる「1 frame の位置ジャンプ」が理論上起こりうる。
  // useLayoutEffect は DOM mutation 後・ブラウザ paint 前に同期実行されるので
  // この隙を構造的に無くす (resize 時の再配置も同様に効く)。
  const initializedEngineRef = useRef<RoomChrome['engine']>(null);
  const prevSlotRef = useRef<{ width: number; height: number } | null>(null);
  const { engine, applyFitZoom, centerViewportAtCurrentZoom, setViewport, viewMapper } = chrome;
  useLayoutEffect(() => {
    const wrapper = scrollRef.current;
    if (engine === null || wrapper === null || rect === null) return;
    const slot = measureSlot(wrapper);
    if (slot.slotWidth <= 0 || slot.slotHeight <= 0) return;

    if (initializedEngineRef.current !== engine) {
      initializedEngineRef.current = engine;
      prevSlotRef.current = { width: slot.slotWidth, height: slot.slotHeight };
      if (isPhoneViewport(readPhoneViewportInput())) {
        applyFitZoom();
      } else {
        centerViewportAtCurrentZoom();
      }
      return;
    }

    const prevSlot = prevSlotRef.current;
    if (prevSlot === null) {
      prevSlotRef.current = { width: slot.slotWidth, height: slot.slotHeight };
      return;
    }
    if (prevSlot.width === slot.slotWidth && prevSlot.height === slot.slotHeight) return;
    setViewport(
      viewportAfterResizeRotated(
        viewMapper.getTransform(),
        prevSlot.width,
        prevSlot.height,
        slot.slotWidth,
        slot.slotHeight,
      ),
    );
    prevSlotRef.current = { width: slot.slotWidth, height: slot.slotHeight };
  }, [engine, rect, scrollRef, applyFitZoom, centerViewportAtCurrentZoom, setViewport, viewMapper]);

  return (
    <>
      {/* 絶対配置の stage ルート。rect が未計測のうちは不可視 (visibility hidden) に
          して描画パイプは生かしたまま (= engine は mount 済、表示だけ次フレームで乗る)。 */}
      <div
        aria-hidden={rect === null ? true : undefined}
        style={{
          position: 'absolute',
          left: rect?.left ?? 0,
          top: rect?.top ?? 0,
          width: rect?.width ?? 0,
          height: rect?.height ?? 0,
          visibility: rect === null ? 'hidden' : 'visible',
          // chrome の空スロットの上に乗せる。modal/drawer は stage より上に出す前提
          // (= chrome 側で z-index を stage より大きくする、§9.7「stage は slot の上・
          //  modal は stage の上」)。
          zIndex: 1,
        }}
      >
        {/* 中: canvas 最大化。overflow:hidden の表示枠 + overlay 群 (2026-09-29
            viewport transform 移行、決定5: 旧 overflow-auto スクロール枠から
            `{tx,ty}` transform 方式に置き換え、スクロールバーは廃止)。
            マスク ON 中は canvas 枠インジケータ (§9.3) を出す。Modern の現状
            ModernLayout.tsx の <main> をそのまま移設 (見た目同一)。 */}
        <main
          ref={scrollRef}
          className={`flex h-full min-h-0 min-w-0 items-start justify-start overflow-hidden ${frameBaseClassName} ${
            maskActive ? frameMaskBorderClassName : frameIdleBorderClassName
          } ${panCursorClass}`}
          aria-label="キャンバス"
          onPointerDown={onWrapperPointerDown}
          onPointerMove={onWrapperPointerMove}
          onPointerUp={onWrapperPointerUp}
          onPointerCancel={onWrapperPointerUp}
        >
          <div
            ref={chrome.viewportLayerRef}
            className="relative"
            // 2026-06-21 (consult、α フィードバック 123 さん) カーソル種別切替: この data
            // 属性で配下の canvas の `cursor: none` (ブラシ円) ⇄ `cursor: var(--oekaki-arrow-
            // cursor, auto)` (矢印) を styles.css が出し分ける。種別 state は共有 chrome
            // (= useRoomChrome) が持つので Classic/Modern 両レイアウトで効く。
            data-oekaki-cursor-type={chrome.brushCursorType}
            // 2026-07-25 方式 B′: ストローク中だけ立つ属性。styles.css が
            // `[data-oekaki-cursor-type="arrow"][data-oekaki-stroking]` のときだけ canvas を
            // `cursor: none` にし、BrushCursorOverlay の矢印グリフだけを見せる (Chromium が
            // pen capture 中ネイティブ矢印を隠す挙動と結果的に揃える + 他ブラウザでの
            // ネイティブ矢印との二重表示も防ぐ)。'brush-ring' モードは元々常時 cursor:none
            // なのでこの属性は無関係 (styles.css 側のセレクタで 'arrow' 限定にしている)。
            data-oekaki-stroking={isStroking ? '' : undefined}
            data-oekaki-locked={connectionLocked ? '' : undefined}
            style={{
              width: state.canvas.width * chrome.displayScale,
              height: state.canvas.height * chrome.displayScale,
              // 2026-07-25 方式 B′: 矢印グリフの SVG data URI + hotspot を CSS Custom
              // Property で流し込む。styles.css には SVG マークアップを複製せず、この
              // 1 プロパティだけを消費する (= arrowCursorGlyph.ts が唯一の定義元)。
              // innerWrapper は canvas の祖先なので custom property は継承で canvas まで届く。
              ...({ '--oekaki-arrow-cursor': ARROW_CURSOR_CSS_VALUE } as React.CSSProperties),
              // 2026-06-09 fix/zoom-scroll-repaint (α-feedback K、§9.7 共有移植) の
              // flex-shrink:0 は 2026-09-29 viewport transform 移行後も引き続き必要:
              // このラッパは表示枠 (`<main>`, `flex items-start justify-start`) の flex
              // item のままなので、既定の flex-shrink:1 では `canvas*zoom` の box が
              // min-content まで潰されうる (= overlay の絶対座標 x*zoom/y*zoom の基準
              // box がズレる)。K 当時の「scrollWidth が伸びない」具体的症状は scroll
              // 自体が無くなったことで解消済みだが、box サイズを宣言通り確保する必要
              // 自体は変わらないので固定は維持する。
              flexShrink: 0,
              // 2026-06-09 fix/zoom-scroll-repaint (α-feedback K 真因修正、§9.7 共有移植)
              // の overflow:clip も維持: K 当時の理由 (= overlay のはみ出しが scroll
              // container の scrollWidth/scrollHeight を水増しし「縮小後に青 padding
              // 領域へスクロールできる」) は 2026-09-29 viewport transform 移行 (決定5)
              // で scroll 自体が無くなり構造的に解消したが、overlay (BrushCursorOverlay
              // のリング等) を canvas 端で視覚的に切る効果は残す (既存の見た目を維持)。
              // canvas 本体 (engine が transform:scale でこの box を満たす) は切れない。
              overflow: 'clip',
              touchAction: 'none',
              userSelect: 'none',
              // pan ドラッグ中は drawing engine への pointer event を遮断 (= pan に流す)。
              pointerEvents: dragModeActive ? 'none' : undefined,
            }}
          >
            <DrawingCanvas
              width={state.canvas.width}
              height={state.canvas.height}
              pen={chrome.pen}
              transport={transport}
              onEngineReady={chrome.onEngineReady}
              canvasRef={chrome.canvasRef}
              // spuit-sample-modes-2026-06-22 §2.1 (モードB 対称トグル): スポイト色pick は
              // 専用の chrome.onSpuitColor へ。内部で onColorChange (色採用) を呼びつつ、
              // モードB で今が消しゴみなら直前ペンへ復帰する (A/C・手動色変更はツール不変)。
              onSpuitColor={chrome.onSpuitColor}
              // §9.6 スポイト持続モード (Modern 専用): ON のとき素クリックで色拾い
              // → onSpuitColor に流れ、ストローク非開始。Classic は常に false
              // (= setSpuitMode を呼ばない) なのでジェスチャのみ挙動を維持。
              spuitMode={chrome.spuitMode}
              // 2026-06-12 スポイト確定シグナル (最近色厳密化): スポイト操作の確定
              // (= pointerup) で「この操作で拾った確定の1色」を chrome 経由で Modern の
              // recordColor へ流す。Classic はハンドラ未登録なので no-op。
              onSpuitCommit={chrome.onSpuitCommit}
              // spuit-sample-modes-2026-06-22: スポイトのサンプル元モード (A/B/C、共有 chrome)。
              // DrawingCanvas が pick のたびに engine.samplePixel へ渡す。両レイアウト共通。
              spuitSampleMode={chrome.spuitSampleMode}
              // モード B で α0 (= 完全透明) を拾ったらツールを消しゴムへ切替 (色は変えない)。
              onSpuitEraser={chrome.onSelectEraserDirect}
              pressureSettings={chrome.currentPressure}
              penOnlyMode={chrome.penOnlyMode}
              onPenAutoEnable={chrome.handlePenAutoEnable}
              leadLineSink={leadLineSource}
              leadEnabled={leadLineEnabled}
              // 画面 ⇔ canvas 座標の変換窓口 (canvas-rotation 段取り 1)。反転 / zoom / pan /
              // (将来) 回転下でも、ポインタを真の canvas 座標へ戻して engine へ渡す
              // (= 表示変換は描画データに混入しない)。
              viewMapper={chrome.viewMapper}
              // 2026-06-13 (feature/preview-perf): 自分のストローク中シグナルを共有 chrome へ。
              // プレビュー (Classic/Modern) が「自分の描画中はサムネ再生成をサスペンド」する。
              // 2026-07-25 方式 B′: 同じシグナルを isStroking ローカル state にも流す
              // (= data-oekaki-stroking 属性 + BrushCursorOverlay の矢印グリフ表示)。
              // handleStrokeActiveChange が両方を呼ぶ (chrome.setStrokeActive は元のまま実行)。
              onStrokeActiveChange={handleStrokeActiveChange}
              {...(onOwnStrokeEnd !== undefined ? { onOwnStrokeEnd } : {})}
              // 2026-06-20 canvas-background-color §8.4 透過確認チェッカー: 背景を 👁 off に
              // した個人ビューのとき canvas 背景を市松に差し替える。共有 chrome 経由なので
              // Classic/Modern 両レイアウトで効く (両者は同じ backgroundVisible state を読む)。
              backgroundVisible={chrome.backgroundVisible}
              // 2026-06-20 canvas-background-color CSS 方式: 背景可視 (👁 ON) のとき canvas の
              // CSS background-color に当てる現在の背景色。engine は bg を焼かず透明に描くので、
              // 未描画部の後ろに見える色は CSS が決める。color 変更は repaint なしに即反映。
              backgroundRgb={chrome.backgroundRgb}
              // M1-c タッチジェスチャ: 2本指パン (scroll 反映) + ピンチ (連続倍率
              // アンカー zoom、M1-a の handleZoomToAtPointer)。touch のみ有効で
              // pen / mouse の経路には影響しない (DrawingCanvas 側で分岐)。
              onPenOnlyFingerIgnored={handlePenOnlyFingerIgnored}
              onGesturePan={onGesturePan}
              onGestureZoomTo={onGestureZoomTo}
              onGestureRotateTo={onGestureRotateTo}
              onTouchGestureAnalytics={onTouchGestureAnalytics}
              locked={connectionLocked}
              onLockedPointerDown={handleLockedPointerDown}
              // 範囲の道具 (範囲の移動・コピー): 選ばれている間だけドラッグを範囲選択 / 浮かせた絵の
              // 移動に流す (手のひら・回転ツール・2 本指の操作はこれまでどおり)。
              regionPointer={chrome.regionToolActive ? chrome.region.pointer : null}
            />
            <NameLabelOverlay
              mode={chrome.nameLabelMode}
              labels={chrome.nameLabels}
              zoom={chrome.displayScale}
              // 2026-06-07 view flip (canvas-flip.md §5): overlay は canvas の反転 transform 下に
              // 無いので、反転中は label の left を `(canvasWidth - x) * zoom` に X 再マップする
              // (文字は鏡像にならない)。
              flipX={chrome.flipX}
              canvasWidth={state.canvas.width}
              // custom-cursor-2026-09-19.md §2.5-2: 描画中の相手の確定済みカーソル。
              cursorForUid={cursorForUid}
            />
            {/* custom-cursor-2026-09-19.md §2.5-4 (time-permitting bonus):
                「作成中バブル」— cursorDrafts が空/未指定なら overlay 自体が
                何も render しない (= 既存挙動を壊さない)。 */}
            <DraftBubbleOverlay
              drafts={cursorDrafts ?? {}}
              lastKnownPositionForUid={lastKnownPositionForUid}
              zoom={chrome.displayScale}
              flipX={chrome.flipX}
              canvasWidth={state.canvas.width}
            />
            {/* §9.8.1 ① 表示専用リード線は **実ペン (drawing-core) ラスタライズ**へ
                格上げ済 (engine.setLead 経由で canvas に直接描く)。旧 SVG 細線
                (LeadLineOverlay) は廃止 (= ここに DOM を出さない)。○リングは残す。 */}
            {/* 2026-07-25 方式 B′ (consult 確定、旧 2026-06-21 案からの改訂):
                以前は 'arrow' のとき overlay を render しなかった (= 完全 unmount) が、
                Chromium が pen capture 中ネイティブ矢印カーソルを隠すため、描画中に
                矢印が消える不具合があった。今は常時 render し、cursorType /
                strokeActive で内部の表示 (リング hover 表示 or ストローク中矢印) を
                切り替える (= component 自体は不変、中身の出し分けのみ)。 */}
            {/* 範囲の道具の間はブラシの輪を出さない (線を描かない。十字カーソルで範囲を選ぶ)。 */}
            {chrome.regionToolActive ? null : (
              <BrushCursorOverlay
                viewMapper={chrome.viewMapper}
                brushWidth={chrome.pen.width}
                zoom={chrome.displayScale}
                cursorType={chrome.brushCursorType}
                strokeActive={isStroking}
                // custom-cursor-2026-09-19.md §2.5-1: 自分の確定済みカスタムカーソル。
                cursor={ownCursor}
                // 作者 staging フィードバック 2026-09-19 follow-up: 自分の画面での表示
                // だけを個人設定で止められる (既定 ON、相手側には影響しない)。
                showOwnCursor={chrome.showOwnCustomCursor}
              />
            )}
          </div>
        </main>
        {/* 再接続中の帯 (ステージ枠内の下端)。旧 App.tsx の「再接続中…」バッジの後継。 */}
        <ReconnectOverlay tapKey={lockedTapKey} />
        {/* 薄く表示中の札 (上端の隅。再接続帯は下端)。1 枚でも 100% 未満のときだけ出る。 */}
        <DimmedLayerBadge
          opacities={chrome.layerOpacities}
          activeLayerId={chrome.pen.layerId}
          onReset={chrome.resetLayerOpacities}
        />
        {/* 長押しで強調中の札 (その下)。pointer-events なし。 */}
        <LayerSoloBadge engine={chrome.engine} />
        {/* 自分のカーソル / タップ位置に出す小札 (PC は追従、タッチは約 1.5 秒)。 */}
        <LockedCursorTag tapKey={lockedTapKey} />
      </div>

      {/* imagepaste の Confirm/Cancel overlay は preview rect を viewMapper で client 座標へ
          変換して position:fixed 的に出る。stage ルートの absolute 枠に閉じ込めず、
          ModernLayout 末尾と同じく舞台外にぶら下げる (viewMapper さえあれば位置は正しく出る)。 */}
      <ImagepasteOverlay
        preview={chrome.imagepaste.preview}
        uploading={chrome.imagepaste.uploading}
        onConfirm={chrome.imagepaste.confirm}
        onCancel={chrome.imagepaste.cancel}
        // 画面 ⇔ canvas 座標の変換窓口: preview rect (真の canvas 座標) を client 座標へ
        // 変換する (反転 / zoom / pan / 将来の回転を mapper が吸収)。
        viewMapper={chrome.viewMapper}
        // imagepaste 貼り先レイヤー (B、imagepaste-layer-affordance §3.1 B2):
        // 貼り先は useImagepaste の **preview スコープ専用 state** (= pasteLayerId)。
        // start() で現在のアクティブレイヤー (pen.layerId) を初期値に種付けし、以降は
        // この overlay チップ (setPasteLayerId) だけが動かす。**pen.layerId は変えない**
        // (= 本人のアクティブ描画レイヤーは不変)。確定時にこの貼り先が request.layerId に乗る。
        // null (= 貼り先未確定 / レイヤー非対応文脈) のときレイヤー UI を出さない。
        // Classic/Modern 共有 (この ImagepasteOverlay は CanvasStage に 1 箇所)。
        activeLayerId={chrome.imagepaste.pasteLayerId ?? undefined}
        onSelectLayer={chrome.imagepaste.setPasteLayerId}
        // 送信前リサンプル方式のリサイズ UI (角ハンドル + % ボタン)。
        naturalSize={chrome.imagepaste.naturalSize}
        canvasSize={{ w: state.canvas.width, h: state.canvas.height }}
        onResize={chrome.imagepaste.setPreviewRect}
      />

      {/* imagepaste 拒否トースト (2026-06-14): rate-limit / サイズ超過 / 非対応形式 /
          権限なし / アップロード失敗 / ID 無効 を**キャンバスの見えている範囲の中央**に
          短命表示する。貼付時はユーザがキャンバスを注視しているため、チャットの system
          行ではなくここに出す。rate-limited は useImagepaste が毎秒カウントダウンし残り
          0 で消す。その他 reason は約 3 秒で自動消滅 (onDismiss)。ImagepasteOverlay と
          同じく canvasRef さえあれば位置は正しく出る (= Classic/Modern 共有、1 箇所)。 */}
      <ImagepasteToastOverlay
        viewMapper={chrome.viewMapper}
        toast={chrome.imagepaste.rejectToast}
        onDismiss={chrome.imagepaste.dismissRejectToast}
      />
      {/* 範囲の移動・コピー: 範囲の枠 / 浮かせた絵 / 範囲の近くのボタン群 (Classic/Modern 共有)。 */}
      <RegionMoveOverlay
        viewMapper={chrome.viewMapper}
        frameRef={scrollRef}
        region={chrome.region}
        canvas={{ w: state.canvas.width, h: state.canvas.height }}
        variant={regionVariant}
      />
      {/* 範囲の移動・コピーの失敗トースト (送信の拒否 / 時間切れ等)。 */}
      <ImagepasteToastOverlay
        viewMapper={chrome.viewMapper}
        toast={chrome.region.toast}
        onDismiss={chrome.region.dismissToast}
      />
      {/* ペン専用モード中に指が無視されたときの通知 (同じ見た目のトーストを流用)。 */}
      <ImagepasteToastOverlay
        viewMapper={chrome.viewMapper}
        toast={fingerToast}
        onDismiss={dismissFingerToast}
        autoDismissMs={PEN_ONLY_FINGER_NOTICE_DISPLAY_MS}
      />
    </>
  );
};
